import { type ComponentType } from "react";

import { Canvas } from "./Canvas";
import {
  CompassCanvas,
  CompassConversationPanel,
  CompassDock,
  CompassInset,
  CompassPanel,
  CompassSidebar,
  CompassWorkspace,
} from "./CompassRegions";
import { CompassRoot } from "./CompassRoot";
import { CompassShellBody } from "./CompassShellBody";
import { ConversationPanel } from "./ConversationPanel";
import { useDesktopShellEngine } from "./desktopShellEngineContext";
import { Dock } from "./Dock";
import { Inset } from "./Inset";
import { Panel } from "./Panel";
import { Root } from "./Root";
import { ShellBody } from "./ShellBody";
import { Sidebar } from "./Sidebar";
import { Workspace } from "./Workspace";

const switchPart = <Props extends object>(
  CirclePart: ComponentType<Props>,
  CompassPart: ComponentType<Props>,
): ComponentType<Props> => {
  const SwitchedPart = (props: Props) => {
    const engine = useDesktopShellEngine();
    const Part = engine === "compass" ? CompassPart : CirclePart;

    return <Part {...props} />;
  };

  return SwitchedPart;
};

const SwitchingDock = Object.assign(switchPart(Dock, CompassDock), {
  Header: switchPart(Dock.Header, CompassDock.Header),
  Body: switchPart(Dock.Body, CompassDock.Body),
  Footer: switchPart(Dock.Footer, CompassDock.Footer),
});

const SwitchingInset = Object.assign(switchPart(Inset, CompassInset), {
  Header: switchPart(Inset.Header, CompassInset.Header),
  Body: switchPart(Inset.Body, CompassInset.Body),
});

const SwitchingSidebar = Object.assign(switchPart(Sidebar, CompassSidebar), {
  Header: switchPart(Sidebar.Header, CompassSidebar.Header),
  Body: switchPart(Sidebar.Body, CompassSidebar.Body),
  Footer: switchPart(Sidebar.Footer, CompassSidebar.Footer),
});

const SwitchingPanel = Object.assign(switchPart(Panel, CompassPanel), {
  Header: switchPart(Panel.Header, CompassPanel.Header),
  Body: switchPart(Panel.Body, CompassPanel.Body),
  Footer: switchPart(Panel.Footer, CompassPanel.Footer),
});

const SwitchingConversationPanel = Object.assign(
  switchPart(ConversationPanel, CompassConversationPanel),
  {
    Header: switchPart(
      ConversationPanel.Header,
      CompassConversationPanel.Header,
    ),
    Body: switchPart(ConversationPanel.Body, CompassConversationPanel.Body),
    Footer: switchPart(
      ConversationPanel.Footer,
      CompassConversationPanel.Footer,
    ),
  },
);

const SwitchingCanvas = Object.assign(switchPart(Canvas, CompassCanvas), {
  Header: switchPart(Canvas.Header, CompassCanvas.Header),
  Body: switchPart(Canvas.Body, CompassCanvas.Body),
  Footer: switchPart(Canvas.Footer, CompassCanvas.Footer),
});

const SwitchingWorkspace = Object.assign(
  switchPart(Workspace, CompassWorkspace),
  {
    Header: switchPart(Workspace.Header, CompassWorkspace.Header),
    Body: switchPart(Workspace.Body, CompassWorkspace.Body),
    Footer: switchPart(Workspace.Footer, CompassWorkspace.Footer),
  },
);

export const Layout = Object.assign(switchPart(Root, CompassRoot), {
  ShellBody: switchPart(ShellBody, CompassShellBody),
  Dock: SwitchingDock,
  Inset: SwitchingInset,
  Sidebar: SwitchingSidebar,
  Panel: SwitchingPanel,
  ConversationPanel: SwitchingConversationPanel,
  Canvas: SwitchingCanvas,
  Workspace: SwitchingWorkspace,
});
